import React, { useMemo, useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { api } from "@/lib/api";
import { FileImage, Maximize2, Minimize2, Printer, Ruler, Satellite } from "lucide-react";
import EngineeringDrawingFrame, { printEngineeringMap } from "@/components/EngineeringDrawingFrame";
import PositionInspector from "./PositionInspector";
import { berthToDrawing, drawingToBerth } from "../lib/positionCoordinates";
import useAuthenticatedObjectUrl from "../lib/useAuthenticatedObjectUrl";
const EsriTerminalMap = React.lazy(() => import("@/components/EsriTerminalMap"));

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const ANNOTATION_COLORS = {
  berth: "#38BDF8",
  zone: "#F59E0B",
  no_go: "#EF4444",
  route: "#10B981",
  label: "#94A3B8",
};
const MOBILE_ASSET_TYPES = new Set(["forklift", "trailer", "truck", "baggage_cart", "bus", "service_vehicle"]);

export default function MapPlanner({ planner, mapFile, terminalId, onAssign, onPosition, onAutoPosition }) {
  const [hoveredAsset, setHoveredAsset] = useState(null);
  const [dragOverShipAsset, setDragOverShipAsset] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [viewMode, setViewMode] = useState("engineering");
  const [showSource, setShowSource] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const [positionPreview, setPositionPreview] = useState(null);
  const [pickingPosition, setPickingPosition] = useState(false);
  const [pickedPoint, setPickedPoint] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(
    () => typeof window === "undefined" ? 600 : window.innerHeight,
  );

  const viewW = 900;
  const viewH = 600;

  const { url: mapImgUrl } = useAuthenticatedObjectUrl(
    mapFile ? () => api.mapFileBlob(mapFile.id) : null,
    [mapFile?.id],
  );
  const isPdf = mapFile?.content_type === "application/pdf";

  useEffect(() => {
    if (terminalId) {
      api.list("map-annotations", { terminal_id: terminalId })
        .then(items => setAnnotations(items.filter(item => ["accepted", "operational"].includes(item.status))))
        .catch(() => setAnnotations([]));
    }
  }, [terminalId]);

  const berth = planner?.berth;
  const outline = planner?.vessel_outline;
  const ship = useMemo(() => planner?.projected_ship_assets || [], [planner?.projected_ship_assets]);
  const terminalAssets = useMemo(() => planner?.terminal_assets || [], [planner?.terminal_assets]);
  const assignments = useMemo(() => planner?.assignments || [], [planner?.assignments]);
  const selectedAssignment = assignments.find(item => item.id === selectedAssignmentId);
  const selectedAsset = selectedAssignment
    ? terminalAssets.find(item => item.id === selectedAssignment.resource_id)
    : null;

  useEffect(() => {
    if (selectedAssignmentId && !assignments.some(item => item.id === selectedAssignmentId)) {
      setSelectedAssignmentId(null);
    }
  }, [assignments, selectedAssignmentId]);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleResize = () => setViewportHeight(window.innerHeight);
    const handleKeyDown = event => {
      if (event.key === "Escape") setIsFullscreen(false);
    };
    document.body.style.overflow = "hidden";
    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullscreen]);

  const assignedByShipAsset = useMemo(() => {
    const m = {};
    assignments.forEach((a) => {
      if (a.type === "asset" && a.ship_asset_id) m[a.ship_asset_id] = a;
    });
    return m;
  }, [assignments]);

  const handleDragOver = (e, shipAssetId) => {
    e.preventDefault();
    setDragOverShipAsset(shipAssetId);
  };
  const handleDrop = (e, shipAssetId) => {
    e.preventDefault();
    e.stopPropagation();
    const terminalAssetId = e.dataTransfer.getData("terminal_asset_id");
    setDragOverShipAsset(null);
    const target = ship.find((item) => item.id === shipAssetId);
    if (terminalAssetId && onAssign) onAssign(
      terminalAssetId, shipAssetId, target ? { x: target.x, y: target.y } : null
    );
  };

  const handleMapDrop = (e) => {
    e.preventDefault();
    const terminalAssetId = e.dataTransfer.getData("terminal_asset_id");
    if (!terminalAssetId || !onAssign) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * viewW;
    const y = ((e.clientY - rect.top) / rect.height) * viewH;
    onAssign(terminalAssetId, null, { x: Math.round(x), y: Math.round(y) });
  };

  const handleMapClick = (e) => {
    if (!pickingPosition) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setPickedPoint({
      x: ((e.clientX - rect.left) / rect.width) * viewW,
      y: ((e.clientY - rect.top) / rect.height) * viewH,
    });
    setPickingPosition(false);
  };

  // Berth polygon (rotated)
  const berthCorners = useMemo(() => {
    if (!berth) return null;
    const orient = berth.orientation_deg || 0;
    const a = (orient * Math.PI) / 180;
    const cos = Math.cos(a), sin = Math.sin(a);
    const rot = (x, y) => [berth.x1 + (x - berth.x1) * cos - (y - berth.y1) * sin, berth.y1 + (x - berth.x1) * sin + (y - berth.y1) * cos];
    return [rot(berth.x1, berth.y1), rot(berth.x2, berth.y1), rot(berth.x2, berth.y2), rot(berth.x1, berth.y2)];
  }, [berth]);

  const esriGraphics = useMemo(() => {
    const graphics = [];
    if (berthCorners) {
      graphics.push({ type: "polygon", points: berthCorners, color: "#64748B", fillColor: "#334155", fillOpacity: 0.16, width: 2, label: berth?.name });
      graphics.push({ type: "polyline", points: [berthCorners[0], berthCorners[1]], color: "#94A3B8", width: 3 });
    }
    if (outline?.corners) {
      graphics.push({ type: "polygon", points: outline.corners, color: "#10B981", fillColor: "#10B981", fillOpacity: 0.2, width: 2, label: `${planner?.vessel?.name || "Vessel"} · LOA ${planner?.vessel?.loa_m || 0}m` });
    }
    terminalAssets.slice(0, 12).forEach(asset => graphics.push({
      type: "point", point: [asset.position_x || 80, asset.position_y || 80], color: "#F59E0B", markerStyle: "square", size: 8, label: asset.name,
      attributes: { terminalAssetId: asset.id },
    }));
    assignments.filter(item => item.type === "asset" && item.planned_position_x != null && item.planned_position_y != null).forEach(item => {
      const asset = terminalAssets.find(candidate => candidate.id === item.resource_id);
      if (!asset || (!asset.requires_positioning && !MOBILE_ASSET_TYPES.has(asset.type))) return;
      graphics.push({ type: "point", point: [item.planned_position_x, item.planned_position_y], color: "#F59E0B", markerStyle: "square", size: 11, label: asset.name, attributes: { terminalAssetId: asset.id } });
    });
    ship.forEach(asset => graphics.push({
      type: "point", point: [asset.x, asset.y], color: "#38BDF8", size: assignedByShipAsset[asset.id] ? 12 : 9,
      label: `${asset.name} · ${asset.distance_from_bow_m}m · ${asset.side}`,
      attributes: { dropTarget: true, shipAssetId: asset.id },
    }));
    ship.forEach(asset => {
      const assignment = assignedByShipAsset[asset.id];
      if (!assignment) return;
      const terminalAsset = terminalAssets.find(item => item.id === assignment.resource_id);
      if (!terminalAsset) return;
      const start = [assignment.planned_position_x ?? terminalAsset.position_x ?? 100, assignment.planned_position_y ?? terminalAsset.position_y ?? 100];
      const incompatible = terminalAsset.compatible_ship_asset_types?.length && !terminalAsset.compatible_ship_asset_types.includes(asset.type);
      graphics.push({ type: "polyline", points: [start, [asset.x, asset.y]], color: incompatible ? "#EF4444" : "#10B981", width: 2, dash: incompatible });
    });
    return graphics;
  }, [assignedByShipAsset, assignments, berth, berthCorners, outline, planner?.vessel, ship, terminalAssets]);

  const handleEsriDrop = (point, target, terminalAssetId) => {
    if (!terminalAssetId || !onAssign) return;
    const position = { x: Math.round(point[0]), y: Math.round(point[1]) };
    if (target?.shipAssetId) onAssign(terminalAssetId, target.shipAssetId, position);
    else onAssign(terminalAssetId, null, position);
  };
  const mapHeight = isFullscreen ? viewportHeight : viewH;

  return (
    <div
      className={`w-full panel engineering-map-print-root ${
        isFullscreen ? "fixed inset-0 z-[100] bg-[#050B14]" : "relative"
      }`}
      style={{ minHeight: mapHeight }}
      data-testid="map-planner-workspace"
      data-fullscreen={isFullscreen ? "true" : "false"}
    >
      <div className="absolute top-3 left-3 z-30 flex flex-wrap items-center gap-1.5" data-testid="map-view-controls">
        <button type="button" onClick={() => setViewMode("engineering")} className={`panel px-2.5 py-1.5 text-[10px] font-mono inline-flex items-center gap-1.5 ${viewMode === "engineering" ? "text-sky-300 border-sky-500/50" : "text-slate-400"}`} data-testid="map-view-engineering"><Ruler size={12}/>Engineering plan</button>
        <button type="button" onClick={() => setViewMode("esri")} className={`panel px-2.5 py-1.5 text-[10px] font-mono inline-flex items-center gap-1.5 ${viewMode === "esri" ? "text-sky-300 border-sky-500/50" : "text-slate-400"}`} data-testid="map-view-esri"><Satellite size={12}/>Esri reference only</button>
        {viewMode === "engineering" && mapImgUrl && <button type="button" onClick={() => setShowSource(value => !value)} className={`panel px-2.5 py-1.5 text-[10px] font-mono inline-flex items-center gap-1.5 ${showSource ? "text-amber-300" : "text-slate-400"}`} data-testid="map-toggle-source"><FileImage size={12}/>{showSource ? "Hide" : "Show"} source drawing</button>}
        {viewMode === "engineering" && <button type="button" onClick={printEngineeringMap} className="panel px-2.5 py-1.5 text-[10px] font-mono inline-flex items-center gap-1.5 text-slate-400 hover:text-sky-300" data-testid="map-print-engineering"><Printer size={12}/>Print</button>}
        <button
          type="button"
          onClick={() => setIsFullscreen(value => !value)}
          className="panel px-2.5 py-1.5 text-[10px] font-mono inline-flex items-center gap-1.5 text-slate-200 hover:text-sky-300 border-sky-500/30"
          data-testid="map-toggle-fullscreen"
          aria-pressed={isFullscreen}
          title={isFullscreen ? "Return map to the port-call page" : "Open map as a full-screen workspace"}
        >
          {isFullscreen ? <Minimize2 size={12}/> : <Maximize2 size={12}/>}
          {isFullscreen ? "Exit full screen" : "Full screen"}
        </button>
      </div>
      {viewMode === "esri" && <React.Suspense fallback={<div className="grid place-items-center text-xs font-mono text-slate-400" style={{ minHeight: mapHeight }}>Loading reference imagery…</div>}><EsriTerminalMap
        terminalId={terminalId}
        annotations={annotations}
        extraGraphics={esriGraphics}
        onMapDrop={handleEsriDrop}
        height={mapHeight}
      /></React.Suspense>}
      {viewMode === "engineering" && <svg
        viewBox={`0 0 ${viewW} ${viewH}`}
        className="map-svg engineering-map w-full block"
        preserveAspectRatio="xMidYMid meet"
        data-testid="map-planner-svg"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleMapDrop}
        onClick={handleMapClick}
        style={{
          cursor: pickingPosition ? "crosshair" : "default",
          height: isFullscreen ? mapHeight : "auto",
          maxHeight: isFullscreen ? mapHeight : "none",
        }}
      >
        <EngineeringDrawingFrame
          width={viewW}
          height={viewH}
          scaleMPerPx={berth?.scale_m_per_px || mapFile?.scale_m_per_px || 1}
          berths={berth ? [berth] : []}
          showConfiguredBerths={false}
          title={`${planner?.terminal?.name || "TERMINAL"} · OPERATIONS PLAN`}
          subtitle={`${berth?.name || "UNASSIGNED BERTH"} · LOCAL METRIC GRID`}
        >
        {showSource && mapImgUrl && !isPdf && (
          <image
            href={mapImgUrl}
            x="0"
            y="0"
            width={viewW}
            height={viewH}
            preserveAspectRatio="xMidYMid slice"
            opacity="0.16"
          />
        )}
        {showSource && isPdf && (
          <foreignObject x="0" y="0" width={viewW} height={viewH}>
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ opacity: 0.16, width: viewW, height: viewH, overflow: "hidden" }}>
              <Document file={mapImgUrl} loading={<div style={{ color: "#64748B", fontFamily: "monospace", padding: 12, fontSize: 11 }}>Rendering PDF…</div>}>
                <Page pageNumber={1} width={viewW} renderTextLayer={false} renderAnnotationLayer={false} />
              </Document>
            </div>
          </foreignObject>
        )}
        </EngineeringDrawingFrame>

        {/* Annotation polygons */}
        {annotations.map((ann) => {
          const color = ann.color || ANNOTATION_COLORS[ann.type] || "#64748B";
          const pts = (ann.points || []).map((p) => p.join(",")).join(" ");
          if (!pts) return null;
          if (ann.type === "route") {
            return (
              <g key={ann.id} data-testid={`annotation-${ann.id}`}>
                <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeDasharray="6 3" />
              </g>
            );
          }
          return (
            <g key={ann.id} data-testid={`annotation-${ann.id}`}>
              <polygon
                points={pts}
                fill={ann.type === "no_go" ? "url(#engineering-no-go-hatch)" : color}
                fillOpacity={ann.type === "no_go" ? 0.18 : 0.10}
                stroke={color}
                strokeWidth="1.5"
                strokeDasharray={ann.type === "zone" ? "4 4" : ""}
              />
              {ann.name && ann.points[0] && (
                <text className="ship-asset-label" x={ann.points[0][0] + 6} y={ann.points[0][1] + 14} fill={color} fontSize="10">
                  {ann.name}
                </text>
              )}
            </g>
          );
        })}

        {/* quay edge line — rotated with berth */}
        {berth && berthCorners && (
          <line
            x1={berthCorners[0][0]}
            y1={berthCorners[0][1]}
            x2={berthCorners[1][0]}
            y2={berthCorners[1][1]}
            stroke="#475569"
            strokeWidth="3"
          />
        )}

        {/* Berth polygon (rotated) */}
        {berth && berthCorners && (
          <g>
            <polygon
              className="berth-rect"
              points={berthCorners.map((c) => c.join(",")).join(" ")}
            />
            <text className="berth-label" x={berthCorners[3][0] + 6} y={berthCorners[3][1] - 6}>
              {berth.name} · {berth.length_m}m · {berth.depth_m}m{berth.orientation_deg ? ` · ${berth.orientation_deg}°` : ""}
            </text>
          </g>
        )}

        {/* Vessel outline (rotated polygon) */}
        {outline && outline.corners && (
          <g>
            <polygon
              className="vessel-rect"
              points={outline.corners.map((c) => c.join(",")).join(" ")}
            />
            <polygon
              className="vessel-bow"
              points={`${outline.corners[0][0]},${outline.corners[0][1]} ${outline.corners[3][0]},${outline.corners[3][1]} ${outline.corners[0][0] + (outline.corners[3][0] - outline.corners[0][0]) * 0.5 - (outline.corners[1][1] - outline.corners[0][1]) * 0.06},${outline.corners[0][1] + (outline.corners[3][1] - outline.corners[0][1]) * 0.5 + (outline.corners[1][0] - outline.corners[0][0]) * 0.06}`}
            />
            <text x={outline.corners[0][0] + 8} y={outline.corners[0][1] - 6} className="ship-asset-label" fill="#F1F5F9">
              {planner?.vessel?.name} · LOA {planner?.vessel?.loa_m}m
            </text>
          </g>
        )}

        {/* Terminal asset positions (small markers, names in palette) */}
        {terminalAssets.slice(0, 12).map((t) => (
          <g key={t.id} data-testid={`terminal-asset-marker-${t.id}`} transform={`translate(${t.position_x || 80}, ${t.position_y || 80})`}>
            <rect x="-4" y="-4" width="8" height="8" className="terminal-asset-marker" />
            <title>{t.name} · {t.type}</title>
          </g>
        ))}

        {/* Per-port-call positions for mobile equipment */}
        {assignments.filter((a) => a.type === "asset" && a.planned_position_x != null && a.planned_position_y != null).map((a) => {
          const asset = terminalAssets.find((item) => item.id === a.resource_id);
          if (!asset || (!asset.requires_positioning && !MOBILE_ASSET_TYPES.has(asset.type))) return null;
          const scale = berth?.scale_m_per_px || 1;
          const clearance = asset.clearance_m || 0;
          const footprintLength = (Math.max(asset.length_m || 0, 4) + clearance * 2) / scale;
          const footprintWidth = (Math.max(asset.width_m || 0, 4) + clearance * 2) / scale;
          const orientation = a.planned_orientation_deg || 0;
          return (
            <g
              key={`planned-${a.id}`}
              data-testid={`planned-asset-${a.id}`}
              onClick={(event) => {
                event.stopPropagation();
                setSelectedAssignmentId(a.id);
                setPickedPoint(null);
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                setSelectedAssignmentId(a.id);
                setPickedPoint(null);
              }}
              role="button"
              tabIndex="0"
              aria-label={`Edit exact position for ${asset.name}`}
              style={{ cursor: "pointer" }}
            >
              <rect x={a.planned_position_x - footprintLength / 2} y={a.planned_position_y - footprintWidth / 2} width={footprintLength} height={footprintWidth} transform={`rotate(${orientation} ${a.planned_position_x} ${a.planned_position_y})`} fill="#D97706" fillOpacity="0.12" stroke="#B45309" strokeDasharray="4 2" />
              <rect x={a.planned_position_x - 5} y={a.planned_position_y - 5} width="10" height="10" fill={selectedAssignmentId === a.id ? "#38BDF8" : "#F59E0B"} />
              <text x={a.planned_position_x + 9} y={a.planned_position_y + 4} fill="#78350F" fontFamily="IBM Plex Mono, monospace" fontSize="10">{asset.name}</text>
            </g>
          );
        })}

        {selectedAssignment && positionPreview && (() => {
          const local = drawingToBerth(berth, positionPreview.x, positionPreview.y);
          const quayPoint = local ? berthToDrawing(berth, local.chainage, 0) : null;
          return (
            <g data-testid="position-preview" pointerEvents="none">
              {quayPoint && <line x1={quayPoint.x} y1={quayPoint.y} x2={positionPreview.x} y2={positionPreview.y} stroke="#38BDF8" strokeWidth="1.5" strokeDasharray="4 3"/>}
              <circle cx={positionPreview.x} cy={positionPreview.y} r="9" fill="none" stroke="#38BDF8" strokeWidth="2"/>
              <line x1={positionPreview.x - 13} y1={positionPreview.y} x2={positionPreview.x + 13} y2={positionPreview.y} stroke="#38BDF8"/>
              <line x1={positionPreview.x} y1={positionPreview.y - 13} x2={positionPreview.x} y2={positionPreview.y + 13} stroke="#38BDF8"/>
              {local && <text x={positionPreview.x + 12} y={positionPreview.y - 10} fill="#38BDF8" fontFamily="IBM Plex Mono, monospace" fontSize="9">{local.chainage.toFixed(1)}m · {Math.abs(local.offset).toFixed(1)}m {local.offset < 0 ? "water" : "apron"}</text>}
            </g>
          );
        })()}

        {/* Ship asset markers */}
        {ship.map((s, idx) => {
          const isDragOver = dragOverShipAsset === s.id;
          const assigned = assignedByShipAsset[s.id];
          const labelDy = -22 - (idx % 4) * 18;
          return (
            <g
              key={s.id}
              onMouseEnter={() => setHoveredAsset(s.id)}
              onMouseLeave={() => setHoveredAsset(null)}
              onDragOver={(e) => handleDragOver(e, s.id)}
              onDragLeave={() => setDragOverShipAsset(null)}
              onDrop={(e) => handleDrop(e, s.id)}
              data-testid={`ship-asset-marker-${s.id}`}
            >
              {isDragOver && (
                <circle cx={s.x} cy={s.y} r="22" fill="rgba(56,189,248,0.18)" stroke="#38BDF8" strokeDasharray="3 3" />
              )}
              {assigned && (
                <circle cx={s.x} cy={s.y} r="14" className="pulse-marker" fill="#10B981" opacity="0.35" />
              )}
              <line x1={s.x} y1={s.y} x2={s.x} y2={s.y + labelDy + 18} stroke="#38BDF8" strokeWidth="0.5" opacity="0.5" />
              <circle className="ship-asset-marker" cx={s.x} cy={s.y} r="6" />
              <rect x={s.x + 8} y={s.y + labelDy - 4} width={Math.max(s.name.length * 6 + 10, 100)} height="26" fill="#0E1522" stroke="#1E293B" />
              <text className="ship-asset-label" x={s.x + 12} y={s.y + labelDy + 7}>
                {s.name}
              </text>
              <text className="ship-asset-label" x={s.x + 12} y={s.y + labelDy + 18} opacity="0.65" fontSize="9">
                {s.type} · {s.distance_from_bow_m}m · {s.side}
              </text>
            </g>
          );
        })}

        {/* Connection lines */}
        {ship.map((s) => {
          const a = assignedByShipAsset[s.id];
          if (!a) return null;
          const ta = terminalAssets.find((t) => t.id === a.resource_id);
          if (!ta) return null;
          const tx = a.planned_position_x ?? ta.position_x ?? 100;
          const ty = a.planned_position_y ?? ta.position_y ?? 100;
          const incompatible = ta.compatible_ship_asset_types?.length &&
            !ta.compatible_ship_asset_types.includes(s.type);
          return (
            <line
              key={`conn-${a.id}`}
              x1={tx}
              y1={ty}
              x2={s.x}
              y2={s.y}
              className={incompatible ? "conn-line-bad" : "conn-line"}
            />
          );
        })}
      </svg>}

      {/* Floating legend */}
      <div className="absolute top-14 right-3 z-20 panel p-3 text-[11px] space-y-1.5 font-mono">
        <div className="text-slate-500 uppercase tracking-widest text-[10px] mb-1">Legend</div>
        <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-sky-400"></span> Ship asset</div>
        <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-amber-400"></span> Terminal asset</div>
        <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-emerald-400"></span> Bow</div>
        <div className="flex items-center gap-2"><span className="inline-block w-3 h-1 bg-emerald-400"></span> Valid link</div>
        <div className="flex items-center gap-2"><span className="inline-block w-3 h-1 bg-red-500"></span> Invalid link</div>
        {annotations.length > 0 && <div className="border-t border-slate-700 mt-1 pt-1 text-slate-500 text-[10px]">{annotations.length} annotations</div>}
      </div>
      {selectedAssignment && selectedAsset && onPosition && onAutoPosition && (
        <PositionInspector
          assignment={selectedAssignment}
          asset={selectedAsset}
          berth={berth}
          pickedPoint={pickedPoint}
          disabled={planner?.port_call?.status !== "draft"}
          onClose={() => {
            setSelectedAssignmentId(null);
            setPositionPreview(null);
            setPickingPosition(false);
          }}
          onApply={onPosition}
          onAutoPosition={onAutoPosition}
          onPreviewChange={setPositionPreview}
          onRequestMapPick={() => setPickingPosition(true)}
        />
      )}
    </div>
  );
}
